import React, { useEffect, useState } from "react";
import Select, { MultiValue, SingleValue } from "react-select";
import TaskCreationLKValues from "../../../../../interfaces/lkValues/TaskCreationLKValues";
import { useAuth } from "../../../../../firebase/AuthProvider";
import { getTaskLKValues } from "../../../../../services/taskServices/TaskLKService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAngleDown,
    faAngleUp,
    faAnglesUp,
} from "@fortawesome/free-solid-svg-icons";
import NewTemplateTask from "../../../../../interfaces/templateTask/NewTemplateTask";
import Banner from "../../../../ui/Banner/Banner";
import TemplateTaskOption from "../../../../../interfaces/templateTask/TaskGroupOption";
import { getTaskGroupOptions } from "../../../../../services/templateService/TemplateTaskService";
import Role from "../../../../../interfaces/identity/Role";
import { getRoles } from "../../../../../services/userServices/UserRolesService";
import { Button } from "react-bootstrap";
import Tag from "../../../../../interfaces/tag/Tag";
import { getAllTags } from "../../../../../services/tagServices/TagService";
import { toast } from "react-toastify";
import TextEditor from "../../../textEditor/TextEditor";

interface OptionType {
    value: string;
    label: string;
}

interface TemplateTaskCreationProps {
    setValue: (id: NewTemplateTask) => void;
}

const TemplateTaskCreation: React.FC<TemplateTaskCreationProps> = ({
    setValue,
}) => {
    const { currentUser, currentOrganization } = useAuth();

    const [taskOrganizationOptions, setTaskOrganizationOptions] = useState<
        OptionType[]
    >([]);

    const [taskTypeOptions, setTaskTypeOptions] = useState<OptionType[]>([]);
    const [taskGroupSelectOptions, setTaskGroupSelectOptions] = useState<
        OptionType[]
    >([]);
    const [taskSubTypeOptions, setTaskSubTypeOptions] = useState<OptionType[]>(
        []
    );
    const [waiverTypeOptions, setWaiverTypeOptions] = useState<OptionType[]>([]);
    const [userRoleTypeOptions, setUserRoleTypeOptions] = useState<OptionType[]>(
        []
    );

    // State for tags
    const [allTags, setAllTags] = useState<Tag[]>([]);
    const [formattedTags, setFormattedTags] = useState<OptionType[]>([]);

    const [newTask, setNewTask] = useState<NewTemplateTask>({
        title: "",
        description: "",
        taskOrganization: "",
        taskType: "",
        assignmentType: "",
        taskSubType: "",
        dueDateCalculationType: "",
        dueDateOffset: "",
        creationDateCalculationType: "",
        creationDateOffset: "",
        creationDateCron: "",
        dueDateCron: "",
        priority: "Low",
        waiverId: "",
        taskGroup: "",
        role: "",
        tags: [],
        information:""
    });

    const assignmentTypeOptions = [
        {
            value: "participants",
            label: "Participants",
        },
        {
            value: "employees",
            label: "Employees",
        },
        {
            value: "employeesWithRole",
            label: "Employees with role",
        },
        {
            value: "sc",
            label: "SC",
        },
        {
            value: "participantsWithWaiver",
            label: "Participants with waiver",
        },
    ];

    const [lkOptions, setLkOptions] = useState<
        TaskCreationLKValues | null | undefined
    >();

    const [taskGroupOptions, setTaskGroupOptions] = useState<
        TemplateTaskOption[] | null
    >();

    const [userRoleOptions, setUserRoleOptions] = useState<Role[] | null>();

    useEffect(() => {
        // Fetch the task with taskId and update the state
        const fetchLkValues = async () => {
            if (currentUser !== null) {
                const authToken: string = await currentUser.getIdToken();
                const fetchedValues: TaskCreationLKValues | null =
                    await getTaskLKValues(authToken);
                const fetchedTemplateOptions: TemplateTaskOption[] | null =
                    await getTaskGroupOptions(authToken);
                const fetchedUserRoleOptions: Role[] | null = await getRoles(authToken);
                setLkOptions(fetchedValues);
                setUserRoleOptions(fetchedUserRoleOptions);
                setTaskGroupOptions(fetchedTemplateOptions);
            }
        };

        if (lkOptions) {
            const formattedTaskOrganizations = lkOptions.taskOrganizations.map(
                (org) => ({
                    value: org.id.toString(),
                    label: org.type,
                })
            );
            const formattedTaskTypes = lkOptions.taskTypes.map((tt) => ({
                value: tt.id.toString(),
                label: tt.type,
            }));
            const formattedTaskSubTypes = lkOptions.taskSubTypes.map((tst) => ({
                value: tst.id.toString(),
                label: tst.type,
            }));
            const formattedWaiverTypes = lkOptions.waivers.map((waiver) => ({
                value: waiver.id.toString(),
                label: waiver.waiverName,
            }));

            setWaiverTypeOptions(formattedWaiverTypes);
            setTaskTypeOptions(formattedTaskTypes);
            setTaskSubTypeOptions(formattedTaskSubTypes);
            setTaskOrganizationOptions(formattedTaskOrganizations);
        }

        if (userRoleOptions) {
            const formattedRoleTypes = userRoleOptions.map((role) => ({
                value: role.name,
                label: role.name,
            }));

            setUserRoleTypeOptions(formattedRoleTypes);
        }
        if (taskGroupOptions) {
            const formattedTaskGroupOptions = taskGroupOptions.map((taskGroup) => ({
                value: taskGroup.taskGroupId.toString(),
                label: taskGroup.taskGroupName,
            }));

            setTaskGroupSelectOptions(formattedTaskGroupOptions);
        }
        if (!lkOptions) {
            fetchLkValues();
        }
    }, [lkOptions]);

    useEffect(() => {
        fetchAllTags();
    }, []);

    // 4) Format the tags for react-select
    useEffect(() => {
        if (allTags.length > 0) {
            const newTagOptions = allTags.map((tag) => ({
                value: tag.id.toString(),
                label: tag.name,
            }));
            setFormattedTags(newTagOptions);
        }
    }, [allTags]);

    const fetchAllTags = async () => {
        try {
            if (!currentUser) return;
            const authToken = await currentUser.getIdToken();
            const tags = await getAllTags(authToken);
            if (tags) {
                setAllTags(tags);
            }
        } catch (error) {
            console.error("Error fetching tags:", error);
        }
    };

    const handleTaskOrganizationChange = (
        selectedOption: SingleValue<OptionType>
    ) => {
        const taskOrganization = selectedOption ? selectedOption.value : "";
        setNewTask({ ...newTask, taskOrganization });
    };

    const handleTaskTypeChange = (selectedOption: SingleValue<OptionType>) => {
        const taskType = selectedOption ? selectedOption.value : "";
        setNewTask({ ...newTask, taskType });
    };

    const handleTaskSubTypeChange = (selectedOption: SingleValue<OptionType>) => {
        const taskSubType = selectedOption ? selectedOption.value : "";
        setNewTask({ ...newTask, taskSubType });
    };

    // ===== NEW: handle Tag changes =====
    const handleTagChanges = (
        selectedOptions: MultiValue<OptionType>,
    ) => {
        // We store the array of numeric IDs (or strings if you prefer).
        const selectedTagIds = selectedOptions.map((opt) => Number(opt.value));
        setNewTask({ ...newTask, tags: selectedTagIds });
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setNewTask({ ...newTask, [name]: value });
    };

    const handleCustomChange = (
        fieldName: keyof NewTemplateTask,
        fieldValue: string | undefined
    ) => {
        setNewTask({ ...newTask, [fieldName]: fieldValue });
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Handle submitting the new task
        if (validateFields()) {
            setValue(newTask);
        }
    };

    const validateFields = () => {
        if (newTask.title.trim() === "") {
            toast.error("Template Task Title is a required field.");
            return false;
        } else if (newTask.description.trim() === "") {
            toast.error("Template Task Description is a required field.");
            return false;
        }else if (newTask.information.trim() === "" ||  !newTask.information) {
            toast.error("Information is a required field.");
            return false;
        } else if (newTask.taskGroup.trim() === "") {
            toast.error("Template Task Group is a required field.");
            return false;
        } else if (newTask.assignmentType === "") {
            toast.error("Template Task Assignment Distribution is a required field.");
            return false;
        } else if (newTask.taskOrganization === "") {
            toast.error("Template Task Organization is a required field.");
            return false;
        } else if (newTask.taskType === "") {
            toast.error("Template Task Type is a required field.");
            return false;
        } else if (newTask.taskSubType === "") {
            toast.error("Template Task Sub Type is a required field.");
            return false;
        } else if (
            newTask.assignmentType === "participantsWithWaiver" &&
            newTask.waiverId === ""
        ) {
            toast.error("An assignment type of `Participant With Waiver` requires a waiver to be selected.");
            return false;
        } else if (
            newTask.assignmentType === "EmployeesWithRole" && newTask.role === ""
        ) {
            toast.error("An employee role is required if Employees With Role option is selected.")
            // setErrorMessage("An employee role is required if Employees With Role option is selected.");
            return false;
        }
        return true;
    };

    const handleInfoChange = (infoText: string) => {
        setNewTask({ ...newTask, information: infoText });
    };
    return (
        <>
            <div className="top border_tittle mby_16">
                <div className="top_bar_tittle">
                    <h2>New Template</h2>
                </div>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="custom_input_flled">
                    <label htmlFor="title">Title</label>
                    <input
                        type="text"
                        id="title"
                        name="title"
                        placeholder="Give your task a title..."
                        value={newTask.title}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="custom_input_flled">
                    <label htmlFor="description">Description</label>
                    <textarea
                        id="description"
                        name="description"
                        placeholder="A brief description of what the task entails..."
                        value={newTask.description}
                        onChange={handleChange}
                        required
                    />
                </div>

                <div className="custom_input_flled">
                    <label htmlFor="info">Information</label>
                    <TextEditor
                        text={newTask.information}
                        setText={handleInfoChange}
                    />
                </div>
                <div className="custom_input_flled">
                    <label htmlFor="taskGroup">Task Group</label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={taskGroupSelectOptions}
                        value={taskGroupSelectOptions.find(
                            (option) => option.value === newTask.taskGroup
                        )}
                        onChange={(selectedValue) =>
                            handleCustomChange("taskGroup", selectedValue?.value)
                        }
                    />
                </div>
                <div className="custom_input_flled">
                    <label htmlFor="assignmentType">Assignment Distribution</label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={assignmentTypeOptions}
                        value={assignmentTypeOptions.find(
                            (option) => option.value === newTask.assignmentType
                        )}
                        onChange={(selectedValue) =>
                            handleCustomChange("assignmentType", selectedValue?.value)
                        }
                    />
                </div>
                {newTask.assignmentType === "participantsWithWaiver" && (
                    <div className="custom_input_flled">
                        <label htmlFor="assignmentType">Waiver Type</label>
                        <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            options={waiverTypeOptions}
                            value={waiverTypeOptions.find(
                                (option) => option.value === newTask.waiverId
                            )}
                            onChange={(selectedValue) =>
                                handleCustomChange("waiverId", selectedValue?.value)
                            }
                        />
                    </div>
                )}
                {newTask.assignmentType === "employeesWithRole" && (
                    <div className="custom_input_flled">
                        <label htmlFor="assignmentType">Role Type</label>
                        <Select
                            className="react-select-container"
                            classNamePrefix="react-select"
                            options={userRoleTypeOptions}
                            value={userRoleTypeOptions.find(
                                (option) => option.value === newTask.role
                            )}
                            onChange={(selectedValue) =>
                                handleCustomChange("role", selectedValue?.value)
                            }
                        />
                    </div>
                )}
                <div className="custom_input_flled">
                    <label htmlFor="taskOrganization">Task Organization</label>
                    <Select
                        id="taskOrganization"
                        name="taskOrganization"
                        value={taskOrganizationOptions.find(
                            (option) => option.value === newTask.taskOrganization
                        )}
                        onChange={handleTaskOrganizationChange}
                        options={taskOrganizationOptions}
                        className="react-select-container"
                        classNamePrefix="react-select"
                    />
                </div>
                {newTask.taskOrganization && (
                    <div className="custom_input_flled">
                        <label htmlFor="taskType">Task Type</label>
                        <Select
                            id="taskType"
                            name="taskType"
                            value={taskTypeOptions.find(
                                (option) => option.value === newTask.taskType
                            )}
                            onChange={handleTaskTypeChange}
                            options={taskTypeOptions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                )}
                {newTask.taskType && (
                    <div className="custom_input_flled">
                        <label htmlFor="taskSubType">Task Sub Type</label>
                        <Select
                            id="taskSubType"
                            name="taskSubType"
                            value={taskSubTypeOptions.find(
                                (option) => option.value === newTask.taskSubType
                            )}
                            onChange={handleTaskSubTypeChange}
                            options={taskSubTypeOptions}
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                )}

                {/* ===== NEW: Tags ===== */}
                <div className="custom_input_flled mb-0">
                    <label htmlFor="tags">Tags <span>(Optional)</span>:
                    </label>
                    <Select
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={formattedTags}
                        isMulti
                        onChange={handleTagChanges}
                        // Pre-select tags if newTemplate.tags is not empty
                        value={formattedTags.filter((tagOption) =>
                            newTask.tags?.includes(Number(tagOption.value))
                        )}
                        menuPortalTarget={document.body}
                        styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        }}
                    />
                </div>

                <div className="form_btn_flx mby_t_24">
                    <Button onClick={(e) => handleSubmit} type="submit">Create Template</Button>
                </div>
            </form>
        </>
    );
};

export default TemplateTaskCreation;
